import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Checkbox,
  CircularProgress,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { useContext, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AdminBentoCategory } from "schemas/admin";
import {
  BentoBrand,
  BentoContact,
  BentoContactResponse,
  CompensationType,
} from "schemas/dashboard";
import { Message, MessageAttachment } from "schemas/email";
import { NONE } from "schemas/forms";
import { CustomEvent } from "schemas/functions";

import Drawer from "components/Drawer";
import EmailThread from "components/EmailThread";
import { fetcherAuth } from "utils/api";
import { openBase64DataInTab } from "utils/files";
import { getEnumOptions } from "utils/form";
import { camelToTitleCase, titleCase } from "utils/string";
import { updateList } from "utils/updateLocalState";

import ImageUpload from "./ImageUpload";
import Countries from "./Metadata/Countries";
import MultiBentoCategorySelect from "./MultiBentoCategorySelect";
import { AdminNewBrand } from "./schemas";
import styles from "./styles";

interface NewBrandProps {
  brands: BentoBrand[];
  setBrands: (brands: BentoBrand[]) => void;
  categoriesById: { [key: number]: AdminBentoCategory };
  handleClose: () => void;
  postSaveAction?: (brand: BentoBrand) => void;
}

type MenuOption = {
  value: string;
  label: string | JSX.Element;
};

const NewBrand = ({
  brands,
  setBrands,
  categoriesById,
  handleClose,
  postSaveAction,
}: NewBrandProps) => {
  const { getToken } = useAuth();

  const { setAlert, setErrorAlert } = useContext(AlertContext);

  const [brand, setBrand] = useState<AdminNewBrand>({
    brandName: "",
    website: "",
    tags: "",
    mainCategory: "",
    categories: [],
    pricePoint: NONE,
    instagramUsername: "",
    instagramFollowerCount: undefined,
    isArchived: false,
    doNotRecommend: false,
    hasValidatedPartnership: false,
    compensationTypes: [],
    bentoBrandLocations: [],
    mainCategoryIds: [],
    secondaryCategoryIds: [],
  });
  const [verifyLoading, setVerifyLoading] = useState<boolean>(false);
  const [saveLoading, setSaveLoading] = useState<boolean>(false);
  const [fetchLoading, setFetchLoading] = useState<boolean>(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [mainImageFile, setMainImageFile] = useState<File | null>(null);
  const [scrapeLoading, setScrapeLoading] = useState<boolean>(false);
  const [regeneratingLoading, setRegeneratingLoading] =
    useState<boolean>(false);
  const [categorizeLoading, setCategorizeLoading] = useState<boolean>(false);
  const [categorizationResult, setCategorizationResult] = useState<string>("");
  const [scrapedContacts, setScrapedContacts] = useState<BentoContact[]>([]);
  const [bentoContactResponses, setBentoContactResponses] = useState<
    BentoContactResponse[] | null
  >(null);
  const [noneFound, setNoneFound] = useState<boolean>(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [responseLoading, setResponseLoading] = useState<boolean>(false);
  const [bentoContactResponse, setBentoContactResponse] = useState<
    BentoContactResponse | undefined
  >();

  const { id } = useParams();

  const onChange = (event: CustomEvent, isMultiple?: boolean) => {
    let { name, value } = event.target;
    if (value === NONE) {
      value = isMultiple ? [] : "";
    }
    setBrand((prev) => {
      return { ...prev, [name]: value };
    });
  };

  const handleCategoriesChange = (categoryIdKey: keyof AdminNewBrand) => {
    return (e: CustomEvent, value: number[] | null) => {
      setBrand((prev) => {
        return {
          ...prev,
          [categoryIdKey]: value,
        };
      });
    };
  };

  const verifyWebsite = async () => {
    setVerifyLoading(true);

    try {
      const res = await fetcherAuth(
        getToken,
        `/api/admin/bento-brands/${brand?.id}/verify`,
        "POST",
      );
      setBrand({
        ...res.bentoBrand,
        mainCategoryIds: brand.mainCategoryIds,
        mainCategories: brand.mainCategoryIds.map(
          (id: number) => categoriesById[id],
        ),
        secondaryCategoryIds: brand.secondaryCategoryIds,
        secondaryCategories: brand.secondaryCategoryIds.map(
          (id: number) => categoriesById[id],
        ),
      });
      updateList(brands, setBrands, res.bentoBrand, "id");
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setVerifyLoading(false);
    }
  };

  const saveChanges = async () => {
    setSaveLoading(true);

    const data = new FormData();
    data.append("brand", JSON.stringify(brand));
    if (mainImageFile) {
      data.append("mainImageFile", mainImageFile);
    }
    if (logoFile) {
      data.append("logoFile", logoFile);
    }
    try {
      const res = await fetcherAuth(
        getToken,
        brand?.id
          ? `/api/admin/bento-brands/${brand?.id}`
          : `/api/admin/bento-brands`,
        brand?.id ? "PUT" : "POST",
        {},
        data,
        false,
        true,
      );
      setBrand(res.bentoBrand);
      updateList(brands, setBrands, res.bentoBrand, "id");
      if (postSaveAction) {
        postSaveAction(res.bentoBrand);
      }
      setAlert("Brand saved successfully", "success");
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setSaveLoading(false);
    }
  };

  const onToggleChange = (event: CustomEvent) => {
    const { name, checked } = event.target;
    setBrand((prev) => {
      return { ...prev, [name]: checked };
    });
  };

  const handleFetchBrand = async () => {
    try {
      setFetchLoading(true);
      const response = await fetcherAuth(
        getToken,
        `/api/admin/bento-brands/${id}`,
        "GET",
      );

      const bentoBrand = response.bentoBrand;
      setBrand(bentoBrand);
    } catch (error) {
      setAlert(error?.message || "Something went wrong", "error");
    } finally {
      setFetchLoading(false);
    }
  };

  const handleFetchBrandResponses = async () => {
    try {
      const response = await fetcherAuth(
        getToken,
        `/api/admin/bento-brands/${id}/responses`,
        "GET",
      );

      setBentoContactResponses(response.bentoContactResponses);
    } catch (error) {}
  };

  const fetchResponse = async (responseId: number) => {
    setResponseLoading(true);
    try {
      let url = `/api/admin/bento-contacts/responses/${responseId}`;
      const { bentoContactResponse, messages } = await fetcherAuth(
        getToken,
        url,
      );
      setMessages(messages);
      setBentoContactResponse(bentoContactResponse);
    } catch (error) {
      setAlert(`An error occurred ${error}`, "warning");
    } finally {
      setResponseLoading(false);
    }
  };

  const openAttachment = async (
    messageId: string,
    attachment: MessageAttachment,
  ) => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/admin/user-integrations/${bentoContactResponse?.userIntegrationId}/messages/${messageId}/attachments/${attachment.attachment_id}`,
        "GET",
      );
      openBase64DataInTab(res.attachment.data, attachment.mime_type);
    } catch (error) {
      setAlert(`An error occurred ${error}`, "warning");
    }
  };

  const handleScrapeContacts = async () => {
    if (!brand?.id) {
      return;
    }

    try {
      setScrapeLoading(true);
      const response = await fetcherAuth(
        getToken,
        `/api/admin/bento-brands/${brand?.id}/verify-and-find-contacts`,
        "POST",
      );
      if (response.contacts.length === 0) {
        setNoneFound(true);
      } else {
        setScrapedContacts(response.contacts);
      }
    } catch (error) {
      setAlert(error?.message || "Something went wrong", "error");
    } finally {
      setScrapeLoading(false);
    }
  };

  const handleCategorize = async (overwrite: boolean) => {
    if (!brand?.id) {
      return;
    }

    try {
      setCategorizeLoading(true);
      setCategorizationResult("");
      const { bentoBrand, mainCategories, secondaryCategories } =
        await fetcherAuth(
          getToken,
          `/api/admin/bento-brands/${brand?.id}/categorize`,
          "POST",
          {},
          {
            overwrite: overwrite,
          },
        );
      setBrand({
        ...bentoBrand,
        mainCategoryIds: mainCategories.map((c: AdminBentoCategory) => c.id),
        secondaryCategoryIds: secondaryCategories.map(
          (c: AdminBentoCategory) => c.id,
        ),
      });
      setCategorizationResult(
        `Categorized with main categories [${mainCategories
          .map((c: AdminBentoCategory) => c.name)
          .join(", ")}] and secondary categories [${secondaryCategories
          .map((c: AdminBentoCategory) => c.name)
          .join(", ")}]`,
      );
    } catch (error) {
      setAlert(error?.message || "Something went wrong", "error");
    } finally {
      setCategorizeLoading(false);
    }
  };

  const handleGeneratingNameAndAbout = async () => {
    if (!brand?.id) {
      return;
    }
    setRegeneratingLoading(true);
    const { bentoBrand } = await fetcherAuth(
      getToken,
      `/api/admin/bento-brands/${brand?.id}/regenerate-about-and-display-name`,
      "POST",
    );
    try {
      setBrand({
        ...brand,
        aboutSummary: bentoBrand.aboutSummary || "",
        displayName: bentoBrand.displayName || "",
      });
    } catch (error) {
      setAlert(error?.message || "Something went wrong", "error");
    } finally {
      setRegeneratingLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      handleFetchBrand();
      handleFetchBrandResponses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const renderTextField = (
    key: string,
    helperText?: string,
    required?: boolean,
    type: string = "text",
    multiRows = 1,
  ) => {
    return (
      <Grid item xs={12} sm={multiRows > 1 ? 12 : 6}>
        <TextField
          fullWidth
          label={camelToTitleCase(key)}
          onChange={onChange}
          value={brand[key as keyof AdminNewBrand] || ""}
          name={key}
          required={required}
          type={type}
          multiline={multiRows > 1}
          rows={multiRows}
        />
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </Grid>
    );
  };

  const renderBooleanField = (key: string) => {
    return (
      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Switch
              name={key}
              checked={Boolean(brand[key as keyof AdminNewBrand])}
              onChange={onToggleChange}
            />
          }
          label={camelToTitleCase(key)}
        />
      </Grid>
    );
  };

  const renderSelect = (key: string, options: string[]) => {
    return (
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel sx={{ mb: 2 }}>{camelToTitleCase(key)}</InputLabel>

          <Select
            name={key}
            value={brand[key as keyof AdminNewBrand] || NONE}
            onChange={(e) => onChange(e, false)}
            label={camelToTitleCase(key)}
          >
            <MenuItem key={NONE} value={NONE}>
              <em>None</em>
            </MenuItem>

            {options.map((option) => (
              <MenuItem key={option} value={option}>
                {titleCase(option)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
    );
  };

  const renderMultipleSelect = (key: string, options: MenuOption[]) => {
    return (
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel sx={{ mb: 2 }}>{camelToTitleCase(key)}</InputLabel>
          <Select
            name={key}
            multiple
            value={brand[key as keyof AdminNewBrand] || []}
            onChange={(e) => onChange(e, true)}
            // @ts-ignore
            renderValue={(selected) => selected?.join(", ")}
            label={camelToTitleCase(key)}
          >
            {options.map((menuOption) => (
              <MenuItem key={menuOption.value} value={menuOption.value}>
                <Checkbox
                  checked={brand?.compensationTypes?.includes(
                    menuOption.value as CompensationType,
                  )}
                />
                <ListItemText primary={menuOption.label} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
    );
  };

  return (
    <Drawer
      open={true}
      width={{ xs: "auto", md: 800, lg: 1000 }}
      onClose={handleClose}
    >
      <DialogTitle sx={styles.dialogTitleText}>
        <Grid container alignItems="center" justifyContent={"space-between"}>
          <Button
            color="secondary"
            onClick={handleClose}
            sx={styles.actionButton}
            size="small"
          >
            Close
          </Button>

          <Box>
            <Typography component="span" noWrap sx={styles.brandName}>
              {brand?.brandName}
            </Typography>
          </Box>
          <Box>
            {brand?.id && (
              <LoadingButton
                variant="outlined"
                color="secondary"
                sx={{ mr: 1 }}
                onClick={verifyWebsite}
                loading={verifyLoading}
                disableElevation
                size="small"
              >
                Re-Verify Website
              </LoadingButton>
            )}
            <LoadingButton
              variant="contained"
              sx={styles.actionButton}
              onClick={saveChanges}
              loading={saveLoading}
              disableElevation
              size="small"
            >
              Save
            </LoadingButton>
          </Box>
        </Grid>
      </DialogTitle>
      {fetchLoading ? (
        <CircularProgress />
      ) : (
        <DialogContent sx={styles.dialogContent}>
          <Grid container rowSpacing={2} columnSpacing={2} sx={{ mt: 2 }}>
            <ImageUpload
              setAlert={setAlert}
              file={logoFile}
              setFile={setLogoFile}
              imageUrl={brand.logoUrl}
              isRounded
              uploadText="Upload Logo"
            />
            <ImageUpload
              setAlert={setAlert}
              file={mainImageFile}
              allowPaste
              setFile={setMainImageFile}
              imageUrl={brand.mainImageUrl}
              isRounded={false}
              uploadText="Upload Header Image"
            />
            {brand?.id && renderTextField("aboutSummary", "", true, "text", 5)}
            {renderTextField("brandName", "", true)}
            {renderTextField("displayName", "", false)}
            {renderTextField("website", "", true)}
            {Object.keys(categoriesById).length !== 0 &&
              brand?.mainCategoryIds && (
                <Grid item xs={12} sm={6}>
                  <MultiBentoCategorySelect
                    categoriesById={categoriesById}
                    name="mainCategories"
                    handleAutocompleteChange={handleCategoriesChange(
                      "mainCategoryIds",
                    )}
                    value={brand.mainCategoryIds || []}
                  />
                </Grid>
              )}
            {Object.keys(categoriesById).length !== 0 &&
              brand?.secondaryCategoryIds && (
                <Grid item xs={12} sm={6}>
                  <MultiBentoCategorySelect
                    categoriesById={categoriesById}
                    name="secondaryCategories"
                    handleAutocompleteChange={handleCategoriesChange(
                      "secondaryCategoryIds",
                    )}
                    value={brand.secondaryCategoryIds || []}
                  />
                </Grid>
              )}
            {brand?.id && renderTextField("targetGender", "", false)}
            {brand?.id && renderTextField("tags")}
            {brand?.id && renderTextField("minTargetAge", "", false, "number")}
            {brand?.id && renderTextField("maxTargetAge", "", false, "number")}
            {renderSelect("pricePoint", [
              "budget-friendly",
              "mid-range",
              "premium",
              "luxury",
            ])}
            {renderTextField("countryCode")}
            {renderTextField("instagramUsername")}
            {renderTextField("instagramFollowerCount", "", false, "number")}
            {renderTextField("tiktokUsername")}
            {renderTextField("pinterestUsername")}
            {renderTextField("twitterUsername")}
            {renderTextField("facebookUsername")}
            {renderTextField("linkedinUrl")}
            {renderTextField("youtubeUrl")}
            {renderMultipleSelect(
              "compensationTypes",
              getEnumOptions(CompensationType),
            )}
          </Grid>
          {renderBooleanField("isArchived")}
          {renderBooleanField("doNotRecommend")}
          {renderBooleanField("hasValidatedPartnership")}
          {renderBooleanField("isInternational")}
          {renderBooleanField("hasVerifiedInstagram")}
          {renderBooleanField("worksWithUgc")}
          {renderBooleanField("worksWithInfluencer")}
          {renderBooleanField("hasRespondedPositively")}
          {renderBooleanField("isLocalOnly")}
          {renderTextField("verificationNotes")}
          {renderBooleanField("excludeFromVerification")}
          <Grid item xs={12}>
            Source:{" "}
            {brand.source
              ? brand.source
              : brand.source === null
                ? "null"
                : "undefined"}
          </Grid>
          <Grid item xs={12}>
            Alternate websites: {brand.alternateWebsites}
          </Grid>

          {brand?.id && (
            <Countries brand={brand} setBrand={setBrand} setAlert={setAlert} />
          )}

          <Grid item xs={12} my={2}>
            <Typography variant="h6">Categorization</Typography>
            <LoadingButton
              loading={categorizeLoading}
              disabled={!brand?.id}
              variant="outlined"
              onClick={() => handleCategorize(false)}
              style={{ marginRight: 2 }}
            >
              Categorize Brand
            </LoadingButton>
            <LoadingButton
              loading={categorizeLoading}
              disabled={!brand?.id}
              variant="outlined"
              onClick={() => handleCategorize(true)}
              style={{ marginRight: 1 }}
            >
              Categorize Brand (with overwrite)
            </LoadingButton>
            {categorizationResult && (
              <Typography>{categorizationResult}</Typography>
            )}
          </Grid>

          <Grid item xs={12} my={2}>
            <Typography variant="h6">Display Name + About</Typography>
            <LoadingButton
              loading={regeneratingLoading}
              disabled={!brand?.id}
              variant="outlined"
              onClick={handleGeneratingNameAndAbout}
              style={{ marginRight: 2 }}
            >
              Regenerate
            </LoadingButton>
          </Grid>

          <Grid item xs={12} my={2}>
            <Typography variant="h6">Contacts</Typography>
            <LoadingButton
              loading={scrapeLoading}
              disabled={!brand?.id}
              variant="outlined"
              onClick={handleScrapeContacts}
            >
              Scrape Contacts For This Brand
            </LoadingButton>
            {scrapeLoading && (
              <Typography>
                <em>Scraping contacts.... this might take a few minutes... </em>
              </Typography>
            )}
            {noneFound && (
              <Typography>No contacts found for this brand</Typography>
            )}
            {scrapedContacts.map((contact) => (
              <Typography key={contact.id}>
                {contact.name} - {contact.email}{" "}
                <Link to={`/admin/contacts/${contact.id}`}>View Contact</Link>
              </Typography>
            ))}
          </Grid>
          {brand?.id && bentoContactResponses && (
            <>
              <Typography variant="h5" sx={{ mt: 3 }}>
                Responses for this brand (most recent 20)
              </Typography>
              <Grid container sx={styles.emailRoot}>
                <Grid item container xs={4} sx={styles.wrapper}>
                  {bentoContactResponses?.length === 0 && (
                    <Typography variant="caption">
                      No responses found
                    </Typography>
                  )}
                  {bentoContactResponses?.map((res) => (
                    <Grid key={res.id} item sx={{ mb: 2, mr: 2, p: 1 }} xs={12}>
                      <Card>
                        <CardContent>
                          <Typography>
                            <strong>{res.userIntegration?.email}</strong> (user
                            Id: {res.userIntegration?.userId})
                          </Typography>
                          <Typography>
                            Response types:{" "}
                            <i>{res.responseTypes?.join(", ")}</i>
                          </Typography>
                          <Typography>Thread: {res.gmailThreadId}</Typography>
                        </CardContent>
                        <CardActions>
                          <Button onClick={() => fetchResponse(res.id)}>
                            View
                          </Button>
                        </CardActions>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
                <Grid item xs={8} sx={styles.wrapper}>
                  {!responseLoading && (
                    <EmailThread
                      messages={messages}
                      openAttachment={openAttachment}
                    />
                  )}
                  {responseLoading && <CircularProgress />}
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>
      )}
    </Drawer>
  );
};

export default NewBrand;
